/**
 * Metrics Step (Wizard step 4).
 *
 * Two editable tables — base metrics and derived metrics — seeded from the
 * auto-inference metric candidates (base metrics) loaded into the reducer.
 * Users can edit any cell inline and add/remove rows.
 *
 * "Suggest metrics with AI" button calls a swappable MetricAdvisor. If the
 * tables already have data, a confirmation modal gates the replace to avoid
 * silently wiping user edits (propose-confirm pattern).
 *
 * Form fields only — no API, no AWS.
 */

import React, { useRef, useState } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Table,
  Button,
  Input,
  Textarea,
  Select,
  Checkbox,
  Box,
  Alert,
  Modal,
  StatusIndicator,
} from '@amzn/awsui-components-react';

import {
  AggFunction,
  BaseMetricDraft,
  DerivedMetricDraft,
  MetricAdvisor,
  MetricSuggestion,
  OnboardingConfig,
  ColumnInfo,
} from '../types';
import {
  OnboardingAction,
  BaseMetricField,
  DerivedMetricField,
  OnboardingState,
} from '../onboardingReducer';
import { heuristicAdvisor } from '../metricAdvisor';

// ─── Single source of truth for AggFunction dropdown options ─────────
const AGG_VALUES: readonly AggFunction[] = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'COUNT_DISTINCT'];
const AGG_OPTIONS: ReadonlyArray<{ label: string; value: AggFunction }> = AGG_VALUES.map(
  (v) => ({ label: v, value: v }),
);

export interface MetricsStepProps {
  config: OnboardingConfig;
  columns?: ColumnInfo[];
  dispatch: React.Dispatch<OnboardingAction>;
  /** Override the default heuristicAdvisor (for testing or future agentCoreAdvisor). */
  advisor?: MetricAdvisor;
}

// ─── Base metrics table ──────────────────────────────────────────────────

function baseMetricColumns(dispatch: React.Dispatch<OnboardingAction>) {
  const update = (index: number, field: BaseMetricField, value: string) =>
    dispatch({ type: 'UPDATE_BASE_METRIC', index, field, value });
  const updatePublish = (index: number, value: boolean) =>
    dispatch({ type: 'UPDATE_BASE_METRIC', index, field: 'publish', value });

  return [
    {
      id: 'metricKey',
      header: 'Metric key',
      cell: (item: BaseMetricDraft & { _index: number }) => (
        <Input
          value={item.metricKey}
          onChange={({ detail }) => update(item._index, 'metricKey', detail.value)}
          ariaLabel={`Base metric ${item._index + 1} key`}
        />
      ),
    },
    {
      id: 'exprSql',
      header: 'SQL expression',
      cell: (item: BaseMetricDraft & { _index: number }) => (
        <Input
          value={item.exprSql}
          onChange={({ detail }) => update(item._index, 'exprSql', detail.value)}
          ariaLabel={`Base metric ${item._index + 1} expression`}
        />
      ),
    },
    {
      id: 'agg',
      header: 'Aggregation',
      cell: (item: BaseMetricDraft & { _index: number }) => (
        <Select
          selectedOption={{ label: item.agg, value: item.agg }}
          options={AGG_OPTIONS as Array<{ label: string; value: string }>}
          onChange={({ detail }) =>
            update(item._index, 'agg', detail.selectedOption.value as string)
          }
          expandToViewport
          ariaLabel={`Base metric ${item._index + 1} aggregation`}
        />
      ),
    },
    {
      id: 'filterCondition',
      header: 'Filter (optional)',
      cell: (item: BaseMetricDraft & { _index: number }) => (
        <Input
          value={item.filterCondition ?? ''}
          onChange={({ detail }) => update(item._index, 'filterCondition', detail.value)}
          placeholder="e.g. region = 'NA'"
          ariaLabel={`Base metric ${item._index + 1} filter`}
        />
      ),
    },
    {
      id: 'publish',
      header: 'Expose as metric',
      minWidth: 130,
      cell: (item: BaseMetricDraft & { _index: number }) => (
        // publish is optional with an implicit `true`, so anything but an
        // explicit false is treated as exposed. Unchecking marks the base as a
        // dependency-only helper: it stays in the catalog for derived metrics to
        // reference but gets no auto-generated queryable passthrough.
        <Checkbox
          checked={item.publish !== false}
          onChange={({ detail }) => updatePublish(item._index, detail.checked)}
          ariaLabel={`Expose base metric ${item._index + 1} as a queryable metric`}
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      minWidth: 100,
      cell: (item: BaseMetricDraft & { _index: number }) => (
        <Button
          variant="inline-link"
          iconName="remove"
          onClick={() => dispatch({ type: 'REMOVE_BASE_METRIC', index: item._index })}
          ariaLabel={`Remove base metric ${item._index + 1}`}
        >
          Remove
        </Button>
      ),
    },
  ];
}

// ─── Derived metrics table ───────────────────────────────────────────────

function derivedMetricColumns(dispatch: React.Dispatch<OnboardingAction>) {
  const update = (index: number, field: DerivedMetricField, value: string) =>
    dispatch({ type: 'UPDATE_DERIVED_METRIC', index, field, value });

  return [
    {
      id: 'derivedMetricKey',
      header: 'Derived metric key',
      cell: (item: DerivedMetricDraft & { _index: number }) => (
        <Input
          value={item.derivedMetricKey}
          onChange={({ detail }) => update(item._index, 'derivedMetricKey', detail.value)}
          ariaLabel={`Derived metric ${item._index + 1} key`}
        />
      ),
    },
    {
      id: 'baseMetricsCsv',
      header: 'Base metrics (CSV)',
      cell: (item: DerivedMetricDraft & { _index: number }) => (
        <Input
          value={item.baseMetricsCsv}
          onChange={({ detail }) => update(item._index, 'baseMetricsCsv', detail.value)}
          placeholder="metric_a,metric_b"
          ariaLabel={`Derived metric ${item._index + 1} base metrics`}
        />
      ),
    },
    {
      id: 'exprSql',
      header: 'SQL expression',
      cell: (item: DerivedMetricDraft & { _index: number }) => {
        // Auto-grow the SQL box to fit its content. Cloudscape's Textarea has no
        // autosize, so derive `rows` from newline count + rough character wrap
        // (~60 chars/line). SQL expressions are usually short, so clamp 1–6.
        const sql = item.exprSql || '';
        const lineCount = (sql.match(/\n/g) || []).length + 1;
        const charLines = Math.ceil(sql.length / 60);
        const rows = Math.min(6, Math.max(1, Math.max(lineCount, charLines)));
        return (
          <Textarea
            value={item.exprSql}
            onChange={({ detail }) => update(item._index, 'exprSql', detail.value)}
            rows={rows}
            ariaLabel={`Derived metric ${item._index + 1} expression`}
          />
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item: DerivedMetricDraft & { _index: number }) => (
        <Input
          value={item.description}
          onChange={({ detail }) => update(item._index, 'description', detail.value)}
          ariaLabel={`Derived metric ${item._index + 1} description`}
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      minWidth: 100,
      cell: (item: DerivedMetricDraft & { _index: number }) => (
        <Button
          variant="inline-link"
          iconName="remove"
          onClick={() => dispatch({ type: 'REMOVE_DERIVED_METRIC', index: item._index })}
          ariaLabel={`Remove derived metric ${item._index + 1}`}
        >
          Remove
        </Button>
      ),
    },
  ];
}

// ─── Component ───────────────────────────────────────────────────────────

const MetricsStep: React.FC<MetricsStepProps> = ({
  config,
  columns,
  dispatch,
  advisor = heuristicAdvisor,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pendingSuggestion, setPendingSuggestion] = useState<MetricSuggestion | null>(null);

  const baseItems = config.baseMetrics.map((m, i) => ({ ...m, _index: i }));
  const derivedItems = config.derivedMetrics.map((m, i) => ({ ...m, _index: i }));

  // Mirror the latest metrics into a ref so the async handleSuggest reads the
  // CURRENT tables at resolution time, not the values captured when it was
  // invoked. Without this, a row added during the (up to a minute) AI call would
  // be invisible to the closure's has-data check, bypassing the replace-confirm
  // modal and silently overwriting the manual row.
  const metricsRef = useRef({ base: config.baseMetrics, derived: config.derivedMetrics });
  metricsRef.current = { base: config.baseMetrics, derived: config.derivedMetrics };

  const applySuggestion = (suggestion: MetricSuggestion) => {
    dispatch({ type: 'APPLY_SUGGESTED_METRICS', suggestion });
    setWarnings(suggestion.warnings);
  };

  const handleSuggest = async () => {
    setError(null);
    setWarnings([]);
    setLoading(true);
    try {
      const suggestion = await advisor.suggest({
        columns: columns ?? [],
        businessContext: config.businessContext,
        columnNotes: config.columnNotes,
      });
      if (metricsRef.current.base.length > 0 || metricsRef.current.derived.length > 0) {
        setPendingSuggestion(suggestion);
      } else {
        applySuggestion(suggestion);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Suggestion failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {/* Confirmation modal for non-empty tables */}
      {pendingSuggestion && (
        <Modal
          visible={true}
          header="Replace existing metrics?"
          onDismiss={() => setPendingSuggestion(null)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setPendingSuggestion(null)}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    applySuggestion(pendingSuggestion);
                    setPendingSuggestion(null);
                  }}
                >
                  Replace
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          This will replace your current base and derived metrics with the AI suggestion.
        </Modal>
      )}

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
      {warnings.length > 0 && (
        <Alert type="warning" dismissible onDismiss={() => setWarnings([])}>
          {warnings.join(' • ')}
        </Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            counter={`(${config.baseMetrics.length})`}
            description="Each base metric is a column plus an aggregation (e.g. SUM of revenue) the tool can report on. Edit, add, or remove rows. Uncheck “Expose as metric” to keep a base only as a building block for derived metrics (it won’t be separately queryable)."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="gen-ai"
                  onClick={handleSuggest}
                  disabled={loading}
                >
                  {loading ? (
                    <StatusIndicator type="loading">Generating with AI — this can take up to a minute…</StatusIndicator>
                  ) : (
                    'Suggest metrics with AI'
                  )}
                </Button>
                <Button
                  iconName="add-plus"
                  onClick={() => dispatch({ type: 'ADD_BASE_METRIC' })}
                >
                  Add base metric
                </Button>
              </SpaceBetween>
            }
          >
            Base Metrics
          </Header>
        }
      >
        <Table
          items={baseItems}
          columnDefinitions={baseMetricColumns(dispatch)}
          variant="embedded"
          empty={
            <Box textAlign="center" color="inherit">
              No base metrics yet. Add one to continue.
            </Box>
          }
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            counter={`(${config.derivedMetrics.length})`}
            description="Optional metrics computed from base metrics."
            actions={
              <Button
                iconName="add-plus"
                onClick={() => dispatch({ type: 'ADD_DERIVED_METRIC' })}
              >
                Add derived metric
              </Button>
            }
          >
            Derived Metrics
          </Header>
        }
      >
        <Table
          items={derivedItems}
          columnDefinitions={derivedMetricColumns(dispatch)}
          variant="embedded"
          empty={
            <Box textAlign="center" color="inherit">
              No derived metrics (optional).
            </Box>
          }
        />
      </Container>
    </SpaceBetween>
  );
};

export default MetricsStep;
